import { useCallback } from 'react';

import { AncestorIdData, AncestorNameData } from 'modules/domains/foldering';
import { NewFolderModal } from 'ui/indexPages/modals/NewFolderModal';
import { isSelectionContent, isSelectionFolder } from 'utils/contexts/selection';

import { useCampaignFolderModals } from '../hooks/useCampaignFolderModals';
import { useCreateCampaignFolder } from '../hooks/useCreateCampaignFolder';
import { useTreeViewQuery } from '../hooks/useTreeViewQuery';
import { ArchiveCampaignModal } from '../modals/ArchiveCampaignModal';
import { CopyCampaignModal } from '../modals/CopyCampaignModal';
import { CreateCampaignFormProvider, CreateCampaignModal } from '../modals/CreateCampaignModal';
import { DeleteFolderModal } from '../modals/DeleteFolderModal';
import { MoveFolderModal } from '../modals/MoveFolderModal';
import { RenameFolderModal } from '../modals/RenameFolderModal';
import { UnarchiveCampaignModal } from '../modals/UnarchiveCampaignModal';
import { useCampaignQueryContext } from './contexts/CampaignQueryContext';
import { useSelectionContext } from './contexts/SelectionContext';

export const CampaignFolderModals = () => {
  const { folderId, refetch, rootFolderId } = useCampaignQueryContext();
  const { active, clearAll } = useSelectionContext();
  const createCampaignFolder = useCreateCampaignFolder();
  const modals = useCampaignFolderModals();

  const haveSelectedFolderOnly = active.length === 1 && isSelectionFolder(active[0]);
  const selectedCampaigns = active.filter(isSelectionContent).map((c) => c.node);

  /** Create Folder */
  const handleCreateFolderConfirmation = useCallback(
    async (newFolderName: string, newFolderDestination: AncestorIdData & AncestorNameData) => {
      modals.create.toggle(false);
      await createCampaignFolder(newFolderName, newFolderDestination);
      refetch();
    },
    [createCampaignFolder, refetch],
  );

  return (
    <>
      {modals.archive.isShown && (
        <ArchiveCampaignModal
          // @ts-ignore will fix when changing modals
          campaigns={selectedCampaigns}
          onClose={() => modals.archive.toggle(false)}
          refetch={refetch}
          deselectAll={clearAll}
        />
      )}
      {modals.unarchive.isShown && (
        <UnarchiveCampaignModal
          // @ts-ignore will fix when changing modals
          campaigns={selectedCampaigns}
          closeModal={() => modals.unarchive.toggle(false)}
          refetch={refetch}
          deselectAll={clearAll}
        />
      )}

      {modals.create.isShown && (
        <NewFolderModal
          defaultDestinationFolderId={haveSelectedFolderOnly ? active[0].id : folderId}
          onClose={() => modals.create.toggle(false)}
          onConfirm={handleCreateFolderConfirmation}
          rootFolderId={rootFolderId}
          treeViewQuery={useTreeViewQuery}
        />
      )}

      {modals.createCampaign.isShown && (
        <CreateCampaignFormProvider>
          <CreateCampaignModal />
        </CreateCampaignFormProvider>
      )}

      {modals.copy.isShown && <CopyCampaignModal />}
      {modals.delete.isShown && <DeleteFolderModal />}
      {modals.move.isShown && <MoveFolderModal />}
      {modals.rename.isShown && haveSelectedFolderOnly && <RenameFolderModal />}
    </>
  );
};
